import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Fade,
  Flex,
  Icon,
  Input,
  Tag,
  TagLabel,
  Tooltip,
} from '@chakra-ui/react';
import { ReactComponent as SearchIcon } from 'resources/images/search.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { ChakraAvatar } from '../ChakraAvatar';
import { styles } from './styles';
import { ChipInputTag } from './types';

export interface ChipInputProps {
  chips: ChipInputTag[];
  placeholder?: string;
  onSubmit: (chip: ChipInputTag) => void;
  formatChip?: (value: string) => ChipInputTag | null;
  onChangeProp?: (value: string) => void;
  remove: (index: number) => void;
  removeAll: () => void;
}

export const ChipInput = ({
  chips,
  placeholder = '',
  onChangeProp,
  onSubmit,
  remove,
  removeAll,
  formatChip = (displayName) => ({
    displayName,
    inviteType: 'Email',
  }),
}: ChipInputProps) => {
  const inputRef = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState('');
  const prevChips = useRef<ChipInputTag[]>(chips);

  const clearInput = () => {
    setValue('');
    onChangeProp?.('');
    if (inputRef.current?.innerText) {
      inputRef.current.innerText = '';
    }
  };

  const moveCursorToEnd = () => {
    const range = document.createRange();

    if (inputRef.current) {
      range.selectNodeContents(inputRef.current);
      range.collapse();
      const sel = document.getSelection();

      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  useEffect(() => {
    if (chips.length > prevChips.current.length) {
      clearInput();
    }
    moveCursorToEnd();
    prevChips.current = chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chips]);

  const isEmpty = useMemo(() => {
    return value.length === 0 && chips.length === 0;
  }, [value.length, chips]);

  const onClear = () => {
    clearInput();
    removeAll();
    inputRef.current?.focus();
  };

  const onChange = (value: string) => {
    setValue(value);
    onChangeProp?.(value);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter' && value) {
      const chip = formatChip(value);
      if (chip) {
        onSubmit(chip);
        clearInput();
      }
    }
    if (event.key === 'Backspace' && chips.length > 0 && !value) {
      remove(chips.length - 1);
    }
  };

  const handlePaste = (event: any) => {
    event.preventDefault();
    const text = event.clipboardData
      .getData('text/plain')
      .replace(/(\r\n|\n|\r)/gm, '');
    if (inputRef.current) {
      inputRef.current.innerText += text;
    }
    onChange(text);
    moveCursorToEnd();
  };

  const {
    rootSx,
    rootWrapperSx,
    tagSx,
    iconSx,
    inputSx,
    clearIconSx,
    clearAllIconSx,
  } = styles;

  return (
    <Flex width="full" sx={rootSx}>
      <Flex sx={rootWrapperSx} data-group>
        <Icon sx={iconSx} as={SearchIcon} />
        {chips.map((tag, index) => (
          <Tag key={tag.displayName} sx={tagSx} data-testid="search-tag">
            <ChakraAvatar
              colors={tag.avatarColors}
              src={tag.imageSrc}
              initials={tag.initials}
              sx={iconSx}
              mr={1}
              size="xxs"
            />
            <Tooltip
              key={tag.displayName}
              label={tag.displayName}
              isDisabled={tag.displayName.length < 32}
              wordBreak="break-all"
            >
              <TagLabel
                sx={{ display: 'block' }}
                fontWeight={500}
                fontSize="xs"
                title={tag.displayName}
              >
                {tag.displayName}
              </TagLabel>
            </Tooltip>
            <Icon
              sx={clearIconSx}
              as={CloseIcon}
              onClick={() => remove(index)}
              data-testid="clear-chip"
            />
          </Tag>
        ))}
        <Input
          as="span"
          contentEditable
          sx={inputSx}
          ref={inputRef}
          variant="unstyled"
          autoFocus
          value={value}
          maxLength={255}
          onInput={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.textContent || '')
          }
          data-testid="search-input"
          placeholder={isEmpty ? placeholder : ''}
          onKeyDown={onKeyDown}
          onPaste={handlePaste}
        />
        <Fade in={!isEmpty} unmountOnExit>
          <Icon
            sx={clearAllIconSx}
            as={CloseIcon}
            onClick={onClear}
            data-testid="clear-all-chips"
          />
        </Fade>
      </Flex>
    </Flex>
  );
};
